import { Component, Input, OnInit, forwardRef } from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { UntilDestroy } from '@ngneat/until-destroy';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { first } from 'rxjs';
import { CandidateService } from 'src/app/recruiting/candidates/candidate.service';
import { ControlMasks } from '../../validators/control-masks';
import { MatIconModule } from '@angular/material/icon';

@UntilDestroy()
@Component({
    selector: 'nova-npi-textbox',
    templateUrl: './npi-textbox.component.html',
    styleUrls: ['./npi-textbox.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NpiTextboxComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => NpiTextboxComponent),
            multi: true
        },
        provideNgxMask()
    ],
    standalone: true,
    imports: [ReactiveFormsModule, MatIconModule, MatLegacyFormFieldModule, MatLegacyInputModule, NgxMaskDirective]
})
export class NpiTextboxComponent implements OnInit, ControlValueAccessor {
    @Input() requiredValidator: boolean;
    @Input() formGroup: FormGroup;
    @Input() isNpiValidated = false;

    nationalProviderIdentifierMask = ControlMasks.nationalProviderNumnber;
    npiFormControl = new FormControl();
    npi: string;

    constructor(private readonly candidateService: CandidateService) {}

    ngOnInit(): void {
        this.npiFormControl.setValidators(this.getValidators());

        if (this.formGroup) {
            this.formGroup.addControl('npi', this.npiFormControl);
        }
    }

    onChange: any = () => {};
    onTouched: any = () => {};

    writeValue(value: any): void {
        this.npiFormControl.setValue(value);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    updateValue(event: Event): void {
        const newValue = (event.target as HTMLInputElement).value;
        this.npi = newValue;
        this.onChange(newValue);
        this.onTouched();
    }

    validate() {
        if (this.npiFormControl.invalid) {
            return { invalidNpi: true };
        }

        if (this.npiFormControl.value && this.npiFormControl.value !== this.npi) {
            this.npi = this.npiFormControl.value;

            this.candidateService
                .npiIsValid(this.npiFormControl.value)
                .pipe(first())
                .subscribe((x) => {
                    if (x) {
                        this.npiFormControl.updateValueAndValidity();
                    } else {
                        this.npiFormControl.setErrors({ invalidNpi: true });
                    }
                    this.npiFormControl.markAsTouched();
                });
        }

        return null;
    }

    private getValidators(): ValidatorFn[] {
        const validators: ValidatorFn[] = [];

        if (this.requiredValidator) {
            validators.push(Validators.required);
        }

        validators.push(Validators.pattern(/^[1-9]\d{9}$/));
        validators.push(Validators.maxLength(10));

        return validators;
    }
}
